import React, { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { CourseProps, LearningActivity, RegistrationStatus } from '../../common/';
import { useLearningActivitiesByUserId, usePaginatedLearningActivities } from '../../hooks/learningActivity';
import { useLearningGoalTranscriptsByUserId } from '../../hooks/learningGoal';
import { convertLearningActivitiesToCourses } from '../../common';
import { NotificationContext } from '../notifications/NotificationContext';
import { useAuthContext } from '../auth/AuthContext';

interface CourseContextProps {
    courses: CourseProps[];
    loading: boolean;
    updateCourseStatus: (classId: string, status: RegistrationStatus) => void;
    refetchCourses: () => void;
    paginatedLearningActivities: {
        allLearningActivities: LearningActivity[];
        hasMore: boolean;
        loadMore: () => void;
        loading: boolean;
    };
}

export const CourseContext = createContext<CourseContextProps>({
    courses: [],
    loading: true,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateCourseStatus: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    refetchCourses: () => {},
    paginatedLearningActivities: {
        allLearningActivities: [],
        hasMore: false,
        loadMore: () => {},
        loading: false,
    },
});

export const CourseProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [courses, setCourses] = useState<CourseProps[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { showErrorFlashbar } = useContext(NotificationContext);
    const { error, data, loading, refetch: refetchRegistrations } = useLearningActivitiesByUserId();

    // Paginated learning activities hook
    const {
        allLearningActivities,
        hasMore,
        loadMore,
        loading: paginatedLoading,
        error: paginatedError,
        refetch: refetchPaginatedActivities,
    } = usePaginatedLearningActivities({ pageSize: 200 });

    // Assigned-learning-goal activities (TSA). Fetched separately so a failure on this federated
    // path never blocks or fails the main dashboard load; merged in additively when available.
    const { learningGoalActivities, error: learningGoalError, refetch: refetchLearningGoals } = useLearningGoalTranscriptsByUserId();
    const { user } = useAuthContext();

    useEffect(() => {
        if (error) {
            showErrorFlashbar(error.message);
            setIsLoading(false);
            return;
        }
        if (paginatedError) {
            showErrorFlashbar(paginatedError.message);
        }
        // Learning goals are additive and best-effort: surface a real error but never block the list.
        if (learningGoalError) {
            showErrorFlashbar(learningGoalError.message);
        }

        // Only process courses when we have data and we're not currently loading paginated data
        if (data && !loading && !paginatedLoading) {
            const paginatedActivities = allLearningActivities || [];
            const mergedData = { ...data, learningActivities: paginatedActivities };
            const courses = convertLearningActivitiesToCourses(mergedData, user.organizationId, learningGoalActivities);
            // Sort by class startTime, earliest first. Learning-goal activities may arrive with no
            // delivery sessions, so guard against an empty classDays instead of dereferencing [0].
            const startTime = (course: CourseProps): number =>
                course.schedule.classDays[0]?.startDateTime.getTime() ?? Number.POSITIVE_INFINITY;
            courses.sort((a, b) => startTime(a) - startTime(b));
            setCourses(courses);
            setIsLoading(false);
        }
    }, [
        error,
        data,
        paginatedError,
        learningGoalError,
        learningGoalActivities,
        allLearningActivities,
        paginatedLoading,
        loading,
        user.organizationId,
    ]);

    const updateCourseStatus = (classId: string, status: RegistrationStatus) => {
        setCourses(courses.map((course) => (course.id === classId ? { ...course, registrationStatus: status, isProxy: false } : course)));
    };

    const refetchCourses = () => {
        refetchRegistrations();
        refetchPaginatedActivities();
        refetchLearningGoals();
    };

    return (
        <CourseContext.Provider
            value={{
                courses,
                loading: isLoading,
                updateCourseStatus,
                refetchCourses,
                paginatedLearningActivities: {
                    allLearningActivities,
                    hasMore,
                    loadMore,
                    loading: paginatedLoading,
                },
            }}
        >
            {children}
        </CourseContext.Provider>
    );
};
